/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the forma's data life (Sync appendix item 7).

   A master forma is a SHARED LIVE DRAFT, like a master spec: everyone who
   opens it edits one Yjs document (`forma:<id>`, formaCollab.ts); the server
   keeps that document on every change, so nothing is lost to a crash or a
   closed tab. The published columns the Formatter, exports and Apply-to-spec
   read are written ONLY by Publish (a write flagged `collabMirror`, which the
   server gates on `formas.publish`).

   Same hook, same interface as before (`forma`, `dirty`, `saving`, `edit`,
   `save`, `undo`, `redo`, `loadState`, `error`…) — `save` is now an alias of
   `publish`. A brand-new / imported forma has no row yet: it edits a LOCAL
   document (no socket) until its first Publish creates the row, then the
   shared document opens on it.

   Undo is the Yjs UndoManager (this client's edits only, 100 deep),
   registered into the workspace's undo coordinator by the Studio.
   The document binding itself is useSharedForma.ts — the SAME binding the
   Designer's project forma uses (appendix item 9); this hook only owns how
   a master forma is found, created and imported.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import * as Y from 'yjs';
import type { StandardConfig } from '../../shared/types';
import { FormaService } from '../services/formaService';
import { reportError } from '../services/errorLog';
import type { CollabPeer, CollabStatus, LiveBlock } from '../spec-studio/collabState';
import { markDirty, seedForma } from './formaCollab';
import { stripDerived, type FormaChipState } from './formaDraftModel';
import { useSharedForma } from './useSharedForma';

export type StudioLoadState = 'loading' | 'ready' | 'error' | 'not-found';

export interface MasterFormaDraftOpts {
  /** Edit mode is on. false = View: the published forma, inert, and this
   *  window is not announced in anyone's peer chips. Default true. */
  editing?: boolean;
  /** The user holds `formas.edit` (the socket may write; this window may seed
   *  an empty document). Default true. */
  canEdit?: boolean;
  /** The user holds `formas.publish`. Default true. */
  canPublish?: boolean;
  /** The shared document ended under this window (our own Discard, a
   *  colleague's Discard, or an out-of-band write). The hook re-reads the
   *  published forma by itself; the Studio toasts / drops to View. */
  onEnded?: (info: { reset: boolean; own: boolean }) => void;
}

export interface MasterFormaDraft {
  loadState: StudioLoadState;
  error: string | null;
  forma: StandardConfig | null;
  /** The shared draft holds unpublished edits (a brand-new forma: unsaved). */
  dirty: boolean;
  /** A Publish is in flight. */
  saving: boolean;
  edit: (fn: (f: StandardConfig) => StandardConfig) => void;
  /** Alias of `publish` (the old Save). */
  save: () => Promise<StandardConfig | undefined>;
  /** Write the shared draft to the published forma (needs `formas.publish`;
   *  a brand-new forma is created, which needs only the create right). */
  publish: () => Promise<StandardConfig | undefined>;
  /** Throw the shared draft away for everyone: the published forma stays. */
  discardDraft: () => Promise<boolean>;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
  reload: () => void;
  /** Import-as-draft (contract §3 R6, item 1): replace the whole draft with
   *  a harvested config — a NEW, unsaved forma (fresh id, no master/project
   *  lineage). Nothing lands in the Formas library until Publish. */
  importDraft: (cfg: StandardConfig) => void;
  /** People also in this draft (names + cursor colour slot). */
  peers: CollabPeer[];
  status: CollabStatus;
  /** The one chip: Published / Pending publish / Saving… / Offline. */
  chip: FormaChipState;
  canPublish: boolean;
  /** A shared draft exists (the forma has a row); false = brand-new, local only. */
  live: boolean;
  /** The PUBLISHED forma as last read / written (never the draft). */
  published: StandardConfig | null;
  /** Edits that exist only in this window (a brand-new forma not yet
   *  created): the only state that still needs an exit guard. */
  unsaved: boolean;
  /** The socket is refused in a way no retry fixes — drop to View. */
  blocked: LiveBlock | null;
  /** Where other people last touched the sheet: selection key -> cursor slot. */
  peerSelections: Record<string, number>;
  /** Tell the room what this window has selected (selection keys). */
  setSelection: (keys: string[]) => void;
}

export function useMasterFormaDraft(
  formaId: string | null,
  userName: string,
  userId: string | undefined,
  /** Present only for a brand-new forma with nothing saved yet (contract
   *  §3 R6, item 10a) — the Studio skips the list lookup and starts from
   *  this in-memory config instead (CSI-seeded by the caller). */
  seed?: StandardConfig | null,
  opts?: MasterFormaDraftOpts
): MasterFormaDraft {
  const editing = opts?.editing !== false;
  const canEdit = opts?.canEdit !== false;
  const canPublish = opts?.canPublish !== false;
  const optsRef = React.useRef(opts);
  optsRef.current = opts;

  const [loadState, setLoadState] = React.useState<StudioLoadState>('loading');
  const [error, setError] = React.useState<string | null>(null);
  const [published, setPublished] = React.useState<StandardConfig | null>(null);
  const [localDoc, setLocalDoc] = React.useState<Y.Doc | null>(null);
  const [createdId, setCreatedId] = React.useState<string | null>(null);
  const [detached, setDetached] = React.useState(false);

  const reloadTokenRef = React.useRef(0);
  const savedOnceRef = React.useRef(!seed);

  // The shared document lives on an existing row: the seed / import path has
  // none until its first Publish creates it (createdId).
  const liveId = seed || detached ? createdId : formaId;
  const liveIdRef = React.useRef(liveId);
  liveIdRef.current = liveId;

  const openLocal = React.useCallback((cfg: StandardConfig, isDirty: boolean) => {
    const d = new Y.Doc();
    seedForma(d, stripDerived(cfg), 'seed');
    if (isDirty) markDirty(d, true);
    setLocalDoc(d);
  }, []);

  const load = React.useCallback(
    (fresh?: boolean): Promise<void> => {
      // `fresh` = re-read the row of the document that is OPEN (after it
      // ended); a plain load starts over from the props.
      if (!fresh) {
        setCreatedId(null);
        setDetached(false);
        if (seed) {
          savedOnceRef.current = false;
          setPublished(null);
          openLocal(seed, false);
          setLoadState('ready');
          return Promise.resolve();
        }
      }
      const id = fresh ? liveIdRef.current : formaId;
      if (!id) {
        setLoadState('error');
        setError('No forma to open.');
        return Promise.resolve();
      }
      savedOnceRef.current = true;
      if (!fresh) setLocalDoc(null);
      const token = ++reloadTokenRef.current;
      setLoadState((s) => (fresh && s === 'ready' ? s : 'loading'));
      setError(null);
      const read = fresh
        ? FormaService.getFresh(id)
        : FormaService.list().then((all) => all.find((f) => f.id === id));
      return read
        .then((match) => {
          if (reloadTokenRef.current !== token) return;
          if (!match) {
            setLoadState('not-found');
            return;
          }
          setPublished(stripDerived(match));
          setLoadState('ready');
        })
        .catch((err: unknown) => {
          if (reloadTokenRef.current !== token) return;
          reportError('Studio:loadForma', err);
          setError(err instanceof Error ? err.message : String(err ?? 'Could not load the forma'));
          setLoadState('error');
        });
    },
    [formaId, seed, openLocal]
  );

  React.useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formaId, seed]);

  const shared = useSharedForma({
    liveId,
    localDoc,
    published,
    ready: loadState === 'ready',
    userName,
    userId,
    editing,
    canEdit,
    canPublish,
    reloadPublished: () => load(true),
    onPublishedRow: setPublished,
    onPublished: (saved, live) => {
      setPublished(saved);
      // The row exists now: the shared document opens on it.
      if (!live) setCreatedId(saved.id);
    },
    onEnded: (info) => optsRef.current?.onEnded?.(info),
    savedOnceRef,
  });

  const importDraft = React.useCallback(
    (cfg: StandardConfig) => {
      const draft: StandardConfig = {
        ...stripDerived(cfg),
        id: 'new-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
        projectId: undefined,
        sourceFormaId: undefined,
        specId: undefined,
        attachedProjectId: undefined,
        editHistory: undefined,
      };
      savedOnceRef.current = false;
      setPublished(null);
      setCreatedId(null);
      setDetached(true);
      openLocal(draft, true);
      setLoadState('ready');
    },
    [openLocal]
  );

  const sharedEdit = shared.edit;
  const edit = React.useCallback(
    (fn: (f: StandardConfig) => StandardConfig) => {
      sharedEdit(fn);
    },
    [sharedEdit]
  );

  const live = !!liveId;
  const docForma = shared.docForma;
  // A view of a not-yet-seeded document shows the published forma.
  const forma = live ? (editing && docForma ? docForma : (published ?? docForma)) : docForma;

  return {
    loadState,
    error,
    forma,
    dirty: shared.dirty,
    saving: shared.saving,
    edit,
    save: shared.publish,
    publish: shared.publish,
    discardDraft: shared.discardDraft,
    undo: shared.undo,
    redo: shared.redo,
    canUndo: shared.canUndo,
    canRedo: shared.canRedo,
    reload: shared.reopen,
    importDraft,
    peers: shared.peers,
    status: shared.status,
    chip: shared.chip,
    canPublish,
    live,
    published,
    unsaved: !live && shared.dirty,
    blocked: shared.blocked,
    peerSelections: shared.peerSelections,
    setSelection: shared.setSelection,
  };
}
